import { Component, OnInit, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { SharedModule } from '../shared/shared.module';
import { Router, RouterModule } from '@angular/router';
import { ApiService } from '@app/shared/services/api.service';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { UtilService } from '@app/shared/services/util.service';
import { certiplate_logo } from '@app/shared/constants/application-constants';

@Component({
    imports: [CommonModule, FormsModule, SharedModule, RouterModule],
    selector: 'app-releasehistory',
    templateUrl: './releasehistory.component.html',
    styleUrls: ['./releasehistory.component.css'],
    changeDetection: ChangeDetectionStrategy.Default
})
export class ReleasehistoryComponent implements OnInit {
  releaseHistory: any[] = [];
  filteredReleaseHistory: any[] = [];
  releaseNotes: SafeHtml = '';
  errorMessage: string = '';
  tableName: string = 'Release History ';
  Home: string = 'Home ';
  searchQuery: string = '';

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer,
    private router: Router,
    private util: UtilService,
  ) {}

  ngOnInit(): void {
    this.util.showLoader = true;
    this.cdr.detectChanges();
    this.loadReleaseHistory();
  }

  home() {
    this.router.navigate(['/Dashboard'])
  }

  async downloadAsPdf(content: string, version: string): Promise<void> {
    try {
      const rawHtml = await marked(content);
      const sanitizedHtml = this.sanitizer.bypassSecurityTrustHtml(rawHtml);
      
      const tempDiv = document.createElement('div');
      tempDiv.style.width = '800px';
      tempDiv.style.padding = '20px';
      tempDiv.innerHTML = rawHtml;
      document.body.appendChild(tempDiv);

      const canvas = await html2canvas(tempDiv as HTMLElement);
      const pdf = new jsPDF('p', 'mm', 'a4');
      const imgData = canvas.toDataURL('image/png');

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 30;
      const footerHeight = 14;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - headerHeight - footerHeight;
      const imgHeight = canvas.height * contentWidth / canvas.width;
      let heightLeft = imgHeight;
      let position = headerHeight;
      let pageNumber = 1;
      const downloadedOn = new Date().toLocaleString();
      const totalPages = Math.max(1, Math.ceil(imgHeight / contentHeight));

      const drawReleaseBranding = (currentPage: number) => {
        try {
          const logoMaxWidth = 56;
          const logoMaxHeight = 18;
          const logoProps = pdf.getImageProperties(certiplate_logo);
          const logoRatio = logoProps.width / logoProps.height;
          let logoWidth = logoMaxWidth;
          let logoHeight = logoWidth / logoRatio;
          if (logoHeight > logoMaxHeight) {
            logoHeight = logoMaxHeight;
            logoWidth = logoHeight * logoRatio;
          }
          pdf.addImage(certiplate_logo, 'PNG', margin, 5, logoWidth, logoHeight);
        } catch (e) {
          console.error('Certiplate logo error:', e);
        }

        pdf.setDrawColor(52, 73, 94);
        pdf.setLineWidth(0.2);
        pdf.line(margin, 25, pageWidth - margin, 25);
        pdf.line(margin, pageHeight - 11, pageWidth - margin, pageHeight - 11);

        pdf.setFontSize(8);
        pdf.setTextColor(52, 73, 94);
        pdf.setFont('helvetica', 'normal');
        pdf.text(`Downloaded on: ${downloadedOn}`, margin, pageHeight - 6);
        pdf.text(`Page ${currentPage} of ${totalPages}`, pageWidth - margin, pageHeight - 6, { align: 'right' });
        pdf.text('Powered by Certiplate', pageWidth / 2, pageHeight - 6, { align: 'center' });
      };

      drawReleaseBranding(pageNumber);
      pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);
      heightLeft -= contentHeight;

      while (heightLeft > 0) {
        position -= contentHeight;
        pageNumber++;
        pdf.addPage();
        drawReleaseBranding(pageNumber);
        pdf.addImage(imgData, 'PNG', margin, position, contentWidth, imgHeight);
        heightLeft -= contentHeight;
      }

      pdf.save(`Release Note_Admin Portal_${version}.pdf`);
      document.body.removeChild(tempDiv);
    } catch (error) {
      console.error('Error generating PDF:', error);
      this.errorMessage = 'Failed to generate PDF';
      this.cdr.detectChanges();
    }
  }

  async downloadReleaseNote(fileKey: string, version: string): Promise<void> {
    const requestBody = { key: fileKey };

    this.apiService.post('/downloadReleaseNotes', requestBody).subscribe({
      next: async (response: any) => {
        await this.downloadAsPdf(response.body.content, version);
      },
      error: (error) => {
        this.errorMessage = 'Failed to download release note';
        console.error(error);
      }
    });
  }

  loadReleaseHistory(): void {
    const requestBody = { key: 'certiplate-ui/' };
  
    this.apiService.post('/listReleaseNotes', requestBody).subscribe({
      next: (response: any) => {
        if (response && response.body && response.body.files && response.body.files.length > 0) {
          this.releaseHistory = response.body.files.map(file => ({
            key: file.key,
            version: file.key.split('/').pop()?.replace('release-notes-', '').replace('.txt', ''), 
            date: new Date(file.lastModified),
            // Add formatted date for display
            formattedDate: this.formatDate(new Date(file.lastModified))
          }));
          this.releaseHistory.sort((a, b) => b.date.getTime() - a.date.getTime());
          this.filteredReleaseHistory = [...this.releaseHistory];
          this.util.showLoader = false;
  
          this.cdr.detectChanges();
  
          if (this.releaseHistory.length > 0) {
            this.loadReleaseNoteContent(this.releaseHistory[0].key);
          }
        } else {
          this.releaseHistory = [];
          this.filteredReleaseHistory = [];
          this.util.showLoader = false;
          this.cdr.detectChanges();
        }
      },
      error: (error) => {
        this.errorMessage = 'Failed to fetch release history';
        this.util.showLoader = false;
        console.error('API Error:', error);
      }
    });
  }

  // Search functionality
  filterReleaseHistory(): void {
    if (!this.searchQuery.trim()) {
      this.filteredReleaseHistory = [...this.releaseHistory];
      return;
    }

    const query = this.searchQuery.toLowerCase().trim();
    this.filteredReleaseHistory = this.releaseHistory.filter(note => 
      note.version.toLowerCase().includes(query) || 
      this.formatDate(note.date).toLowerCase().includes(query)
    );
  }

  // Method to format date as "DD Month YYYY" (e.g., "15 January 2024")
  formatDate(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'long' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  // Alternative method if you want "DD MMM YYYY" format (e.g., "15 Jan 2024")
  formatDateShort(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString('default', { month: 'short' });
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  }

  async loadReleaseNoteContent(fileKey: string): Promise<void> {
    const requestBody = { key: fileKey };

    this.apiService.post('/downloadReleaseNotes', requestBody).subscribe({
      next: async (response: any) => {
        marked.setOptions({
          gfm: true,
          breaks: true
        });

        const rawHtml = await marked(response.body.content);
        this.releaseNotes = this.sanitizer.bypassSecurityTrustHtml(rawHtml);
        this.cdr.detectChanges();
      },
      error: (error) => {
        this.errorMessage = 'Failed to fetch release note content';
        console.error(error);
      }
    });
  }

  navigateToReleaseNote(noteKey: string) {
    this.router.navigate(['/releasenotes', noteKey]);
  }

  // Optional: Toggle expand functionality if you need it
  toggleExpand(note: any): void {
    note.expanded = !note.expanded;
    this.cdr.detectChanges();
  }
}
